import type {
  ActionFormationDTO,
  EmployeurDTO,
  FormationDTO,
  ModuleDTO,
  SalarieDTO,
} from '@/features/api/extranet/dto/Demande/AF'
import type { FinancementDTO } from '@/features/api/extranet/dto/Demande/shared/FinancementDTO'
import {
  addPreBuilder,
  targetPreBuilder,
  type Flag,
  type ModelValidateurResult,
  type Regles,
} from '@/features/domaine/moteur-regles'
import { LevelRegleEnum } from '@/features/domaine/types/opco/shared'

import type { FichierDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { AdresseDTO, EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import { MSG_INFO_FSE, MSG_INFO_PIC } from '@/features/interface/demande/shared'
import {
  getAge,
  getNbJour,
  isDate,
  isNotNullUndefinedOrEmptyString,
  isNullOrUndefined,
  isNullUndefinedOrEmptyString,
  isNumber,
} from '@/features/shared/tools'
import { PropertyStatus } from '../../property-status'
import {
  checkNir,
  checkNirDateNaissance,
  checkNirSexe,
  checkNirTemporaire,
  isAdherentOuMandatGestion,
  preEquals,
  preEstNirPortail,
  preNotEquals,
  preNotNullOrEmpty,
  rgEstCodePostalFactory,
  rgEstCommuneFactory,
  rgEstDate,
  rgEstMailFactory,
  rgEstMaxLengthFactory,
  rgEstNomOuPrenomFactory,
  rgEstNumeric,
  rgEstNumericMaxFactory,
  rgEstNumericMinFactory,
  rgEstNumTelFactory,
  rgEstObligatoireFactory,
  rgEstVoieFactory,
  rgEstVraiFactory,
} from '../shared'
import { SAISIE_OBLIGATOIRE } from '../shared/messages-erreurs'
import { preNirTemporaire, preNotNirTemporaire } from './preconditions'
import type { ContextReglesAf, ReglesAF } from './types'
import { SOUS_DISPOSITIFS_EXCLUES } from '@/features/domaine/constantes'

// /* Initalisation des helpers */
const targetPre = targetPreBuilder<ActionFormationDTO, ContextReglesAf>()
const addPre = addPreBuilder<ActionFormationDTO, ContextReglesAf>()

/* Declaration de type pour les sous modeles */
type RegleAFMembre<T extends object> = Regles<T, ContextReglesAf, LevelRegleEnum, Flag, ActionFormationDTO>

const addObligatoire = (
  result: ModelValidateurResult<ActionFormationDTO>,
  value: any,
  status: PropertyStatus | undefined,
  propertyName: any,
  condition: boolean = true,
) => {
  if (status === PropertyStatus.Required && condition && isNullUndefinedOrEmptyString(value)) {
    result![propertyName] = SAISIE_OBLIGATOIRE
  }
}

/* Employeur */
/* Etablissement */
const reglesEtablissementEmployeur: RegleAFMembre<EtablissementAdherentDTO> = {}

const reglesEmployeur: RegleAFMembre<EmployeurDTO> = {
  etablissement: reglesEtablissementEmployeur,
  contact: {
    email: {
      ...rgEstMaxLengthFactory(200),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.email?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.telephone?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    },
  },
  tailleEntreprise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.tailleEntreprise?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
}

/* Salaries */
const reglesSalaries: RegleAFMembre<SalarieDTO> = {
  nomNaissance: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  prenom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nirTemporaire: {},
  nir: {
    ...addPre(preNotNirTemporaire, rgEstMaxLengthFactory(13, LevelRegleEnum.Metier)),
    ...rgEstMaxLengthFactory(250),
    'check-nir': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNotNirTemporaire],
      validateur: (value) => checkNir(value!),
      message: 'Le format du NIR est incorrect',
      level: LevelRegleEnum.Metier,
    },
    /*
    'check-nir-api': {
      pre: [
        preNotNullOrEmpty,
        preEstNirPortail,
        preNotNirTemporaire,
        (_, parent) => (parent.nomNaissance?.length ?? 0) > 2,
      ],
      validateur: async (value, parent, _modele, context) => {
        if (['1', '2'].includes(value![0]) === false) return

        const result = await context.services.validerNir(value!, parent.nomNaissance!)
        return result.isValidFormat === true && result.nirDetails !== null
      },
      message: 'Le NIR ne correspond pas au nom de naissance',
      level: LevelRegleEnum.Metier,
    },
    */
    'check-nir-temporaire': {
      pre: [preNotNullOrEmpty, preNirTemporaire, preEstNirPortail],
      validateur: (value) => checkNirTemporaire(value!),
      message: 'Le format du NIR temporaire est incorrect',
      level: LevelRegleEnum.Metier,
    },
    'check-nir-doublon': {
      pre: [preNotNullOrEmpty],
      validateur: (value, parent, model, _context) => {
        return !model.salaries?.some((x) => x !== parent && x.nir === value)
      },
      message: 'Un salarié avec le même NIR a déjà été saisi sur cette demande',
      level: LevelRegleEnum.Technique,
    },
  },
  sexe: {
    controleSexeNir: {
      pre: [preNotNullOrEmpty, preNotNirTemporaire],
      validateur: (value, parent) =>
        isNotNullUndefinedOrEmptyString(parent.nir!) && checkNir(parent.nir!)
          ? checkNirSexe(parent?.nir ?? '', value!)
          : true,
      message: 'Le sexe du stagiaire ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
  },
  dateNaissance: {
    ...rgEstDate,
    controleDateNaissanceNir: {
      pre: [preNotNullOrEmpty, preNotNirTemporaire],
      validateur: (value, parent) =>
        isNotNullUndefinedOrEmptyString(parent.nir!) && checkNir(parent.nir!)
          ? checkNirDateNaissance(parent?.nir ?? '', new Date(value!))
          : true,
      message: 'La date de naissance du stagiaire ne correspond pas au NIR saisi',
      level: LevelRegleEnum.Metier,
    },
    controleMinimum: {
      pre: [preNotNullOrEmpty],
      validateur: (value, _parent, model) => {
        const MAX_DATE = '2100-01-01' as const
        const dateDebutMin =
          model.formation?.modules
            ?.map((x) => x.dateDebut)
            ?.filter(isDate)
            ?.reduce((prev, curr) => (prev! < curr! ? prev : curr), MAX_DATE) ?? MAX_DATE

        if (dateDebutMin === MAX_DATE) return true

        const age = getAge(new Date(value!), new Date(dateDebutMin))
        return age >= 14 && age <= 99
      },
      message: "Le stagiaire doit être agé d'au moins 14 ans à la date de début de formation et au maximum de 99 ans",
      level: LevelRegleEnum.Metier,
    },
  },

  adresse: {
    voie: {
      ...rgEstMaxLengthFactory(38),

      ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    complement: {
      ...rgEstMaxLengthFactory(38),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),

      ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),

      ...rgEstCommuneFactory(LevelRegleEnum.Metier),
    },
  },
  email: {
    ...rgEstMaxLengthFactory(64),
    ...rgEstMailFactory(LevelRegleEnum.Metier),
  },
  telephone: {
    ...rgEstMaxLengthFactory(10),
    ...rgEstNumTelFactory(LevelRegleEnum.Metier),
  },
  numeroDossierCpf: {
    ...rgEstMaxLengthFactory(255),
  },
  natureContrat: {},
  niveauFormation: {},
  tauxHoraireBrut: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salaries?.some((salarie) => salarie.tauxHoraireBrut?.$status === PropertyStatus.Required),
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstNumericMinFactory(-1, LevelRegleEnum.Metier, 'Le taux horaire brut saisi doit être supérieur à 0'),
    ...rgEstNumericMaxFactory(
      200,
      LevelRegleEnum.Metier,
      'Le taux horaire brut saisi est supérieur à 200 €. Merci de corriger ce taux.',
    ),
  },
  pctTauxCharge: {
    ...rgEstNumeric,
    ...rgEstNumericMaxFactory(1.01, LevelRegleEnum.Metier, 'Le taux de charge doit être inférieure à 100%'),
  },
}

/* Fichiers */
const reglesFichiers: RegleAFMembre<FichierDTO> = {}

/* Formation */
const reslesModules: RegleAFMembre<ModuleDTO> = {
  modalitePedagogique: {},
  dureeFoad: {
    ...rgEstNumeric,
    controleMaximum: {
      pre: [preNotNullOrEmpty],
      validateur: (value, parent, _modele) => {
        // console.log(parent.modalitePedagogique)
        if (isNumber(parent.duree) === false || parent.modalitePedagogique !== 'Mixte') return true
        const dureeFoad = Number(value!)
        const duree = Number(parent.duree!)

        return dureeFoad < duree
      },
      message: 'La durée de la formation à distance doit être inférieure à la durée de formation',
      level: LevelRegleEnum.Metier,
    },
  },
  typeQualification: {},
  avecSubrogation: {},
  dateDebut: {
    ...rgEstDate,
    controleMinimumMaximum: {
      pre: [preNotNullOrEmpty],
      validateur: (value, _parent, _modele) => {
        const now = new Date()
        const dateDebut = new Date(value!)
        const annee = dateDebut.getFullYear()
        return annee >= now.getFullYear() - 3 && annee <= now.getFullYear() + 1
      },
      message: 'La date de début de formation ne peut pas être antérieure à n-3 ni supérieure à n+1',
      level: LevelRegleEnum.Metier,
    },
    controleMinimumFse: {
      pre: [preNotNullOrEmpty, targetPre('sousDispositif', preEquals('Fse'))],
      validateur: (value, _parent, _modele) => {
        //const now = getToday()
        const dateMin = new Date('2025-03-22')
        const dateDebut = new Date(value!)
        return dateDebut > dateMin
      },
      //message: 'La date de début ne peut pas être inférieure ou égale à la date du jour',
      message: 'La date de début ne peut pas être inférieure ou égale au 22/03/2025',
      level: LevelRegleEnum.Metier,
    },
  },
  dateFin: {
    ...rgEstDate,
    controleMinimumEgale: {
      pre: [
        preNotNullOrEmpty,
        targetPre('sousDispositif', preEquals('ActionStandard', 'PDC', 'TuteurMAitreApprentissage', 'Fne', 'PicIae')),
      ],
      validateur: (value, parent, _modele) => {
        if (isDate(parent.dateDebut) === false) return true
        const dateDebut = new Date(parent.dateDebut!)
        const dateFin = new Date(value!)
        return dateFin >= dateDebut
      },
      message: 'La date de fin de formation ne peut pas être inférieure à la date de début de formation',
      level: LevelRegleEnum.Metier,
    },
    controleMinimumStrict: {
      pre: [
        preNotNullOrEmpty,
        targetPre(
          'sousDispositif',
          preEquals('BilanCompetences', 'ValidationAcquisExperiences', 'AbondementCpf', 'CleaSocleCompetence', 'Fse'),
        ),
      ],
      validateur: (value, parent, _modele) => {
        if (isDate(parent.dateDebut) === false) return true
        const dateDebut = new Date(parent.dateDebut!)
        const dateFin = new Date(value!)
        return dateFin > dateDebut
      },
      message: 'La date de fin de formation ne peut pas être inférieure ou égale à la date de début de formation',
      level: LevelRegleEnum.Metier,
    },
    /* PRT-1302 */
    controleMaximumFse: {
      pre: [preNotNullOrEmpty, targetPre('sousDispositif', preEquals('Fse'))],
      validateur: (value, _parent, _modele) => {
        const dateMax = new Date("2026-04-29")
        const dateFin = new Date(value!)
        if (dateFin > dateMax)
          return 'La date de fin ne peut pas être supérieure au 29/04/2026.'
      },

      level: LevelRegleEnum.Metier,
    },
    /*
    controleMaximumFse: {
      pre: [preNotNullOrEmpty, targetPre('sousDispositif', preEquals('Fse'))],
      validateur: (value, _parent, _modele) => {
        const today = getToday()
        const dateFin = new Date(value!)

        return dateFin.getFullYear() === today.getFullYear()
          ? undefined
          : `La date de fin ne peut pas être supérieure au 31/12/${today.getFullYear()}.`
      },

      level: LevelRegleEnum.Metier,
    },
    */
  },
  duree: {
    ...rgEstNumeric,
    minimumFpe: {
      pre: [preNotNullOrEmpty, targetPre('sousDispositif', preEquals('AfFpe'))],
      validateur: (value, _parent, _modele) => {
        const duree = Number(value!)
        return duree > 0
      },
      message: 'La durée doit être supérieure ou égale à 30 minutes.',
      level: LevelRegleEnum.Metier,
    },
    minimumAutre: {
      pre: [preNotNullOrEmpty, targetPre('sousDispositif', preNotEquals('AfFpe'))],
      validateur: (value, _parent, _modele) => {
        const duree = Number(value!)
        return duree > 1
      },
      message: 'La durée doit être strictement supérieure à une heure.',
      level: LevelRegleEnum.Metier,
    },

    controleMaximum: {
      pre: [preNotNullOrEmpty, targetPre('sousDispositif', preEquals('BilanCompetences'))],
      validateur: (value, _parent, _modele) => {
        const duree = Number(value!)
        return duree <= 24
      },
      message: 'La durée est de 24 heures maximum',
      level: LevelRegleEnum.Metier,
    },
  },
  dureeJours: {
    ...rgEstNumeric,
    controleMaximum: {
      pre: [preNotNullOrEmpty],
      validateur: (value, parent, _modele) => {
        if (isDate(parent?.dateDebut) && isDate(parent?.dateFin)) {
          const dateDebut = new Date(parent?.dateDebut)
          const dateFin = new Date(parent?.dateFin)

          // nb jour + 1
          const nbJour = getNbJour(dateDebut, dateFin) + 1

          const duree = Number(value!)

          // console.log(duree, nbJour)
          return duree <= nbJour
        }
      },
      message: 'La durée en jours dépasse le nombre de jours de la période de formation.',
      level: LevelRegleEnum.Metier,
    },
  },
  objectifPrestataire: {},
  lieuFormation: {
    voie: {
      ...rgEstMaxLengthFactory(38),
      ...addPre(
        (_value, parent, model, _context) => {
          const adresseDto = <AdresseDTO>(<unknown>parent)
          const module = model.formation?.modules?.find((x) => x.lieuFormation === adresseDto)
          return module!.lieuFormationIdentiqueOf !== true
        },
        {
          ...rgEstVoieFactory(LevelRegleEnum.Metier),
        },
      ),
    },
    complement: {
      ...rgEstMaxLengthFactory(38),
      ...addPre(
        (_value, parent, model, _context) => {
          const adresseDto = <AdresseDTO>(<unknown>parent)
          const module = model.formation?.modules?.find((x) => x.lieuFormation === adresseDto)
          return module!.lieuFormationIdentiqueOf !== true
        },
        {
          // ...rgEstVoieFactory(LevelRegleEnum.Metier),
        },
      ),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, parent, model, _context) => {
          const adresseDto = <AdresseDTO>(<unknown>parent)
          const module = model.formation?.modules?.find((x) => x.lieuFormation === adresseDto)
          return module!.lieuFormationIdentiqueOf !== true
        },
        {
          ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
        },
      ),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, parent, model, _context) => {
          const adresseDto = <AdresseDTO>(<unknown>parent)
          const module = model.formation?.modules?.find((x) => x.lieuFormation === adresseDto)
          return module!.lieuFormationIdentiqueOf !== true
        },
        {
          ...rgEstCommuneFactory(LevelRegleEnum.Metier),
        },
      ),
    },
  },
  fraisPedagogiques: {
    montant: {
      ...rgEstNumeric,
      ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      min: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, model) => {
          // console.log(model.sousDispositif, parent.nature)
          if (
            parent.nature === 'Pedagogique' &&
            model.sousDispositif !== 'PDC' &&
            model.sousDispositif !== 'ActionStandard' &&
            model.sousDispositif !== 'AfFpe'
          )
            return Number(value) > 0

          return true
        },
        message: 'Le montant doit être supérieur à 0',
        level: LevelRegleEnum.Technique,
      },
    },
  },
  fraisAnnexesSalarie: {
    montant: {
      ...rgEstNumeric,
      ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
    },
  },

  /* ERWAN, POUR DESIGNER L'OBJET RNCP C'EST CHAINE VIDE : ""
  rncp: {
    $model: {
      "qui-explose-sur-modele": {
        validateur: (value, model, context) => ({ "": "Boom" })
      }
    }
  }
  */
}

const reglesFormation: RegleAFMembre<FormationDTO> = {
  modules: reslesModules,
}

const reglesFinancement: RegleAFMembre<FinancementDTO> = {
  attestationResteChargeContribution: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.attestationResteChargeContribution?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre)
    ),
  },
  modePriseEnChargeReste: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.modePriseEnChargeReste?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre)
    ),
  },
}

/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfiguration: ReglesAF = {
  validationNotice: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationPiecesJustificatives: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationPiecesJustificatives?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  attestationSurHonneur: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  acceptationCg: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationSalariesSaisies: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationSalariesSaisies?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  sousDispositif: {
    controleFse: {
      pre: [preNotNullOrEmpty, preEquals('Fse')],
      validateur: (_value, _parent, _modele) => {
        // PRT-1302, RÉOUVERTURE JUSQU'AU 29/04/2026
        return true
      },
      message: MSG_INFO_FSE,
      level: LevelRegleEnum.Metier,
    },
    controlePic: {
      pre: [preNotNullOrEmpty, preEquals('PicIae')],
      validateur: (_value, _parent, _modele) => {
        return !SOUS_DISPOSITIFS_EXCLUES.includes('PicIae')
      },
      message: MSG_INFO_PIC,
      level: LevelRegleEnum.Metier,
    },
  },
  attestationCadreInterventionFse: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationCadreInterventionFse?.$status === PropertyStatus.Required,
      rgEstVraiFactory(LevelRegleEnum.Transmettre),
    ),
  },
  attestationEngagementsFse: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationEngagementsFse?.$status === PropertyStatus.Required,
      rgEstVraiFactory(LevelRegleEnum.Transmettre),
    ),
  },
  attestationInformationParticipantsFse: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationInformationParticipantsFse?.$status === PropertyStatus.Required,
      rgEstVraiFactory(LevelRegleEnum.Transmettre),
    ),
  },
  referenceProjet: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.referenceProjet?.$status !== PropertyStatus.NotPresent,
      rgEstMaxLengthFactory(30),
    ),
  },
  employeur: reglesEmployeur,
  salaries: reglesSalaries,
  fichiers: reglesFichiers,
  formation: reglesFormation,
  financement: reglesFinancement,
  $model: {
    controleNbSalarie: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}
        if (context.typeContext === TypesContextesAutorisesEnum.Adherent && model.salaries?.length == 0) {
          result[`salaries`] = 'Un salarié est obligatoire'
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleObligatoireSalarie: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}

        for (const [index, prop] of model.salaries!.entries()) {
          addObligatoire(
            result,
            prop.nomNaissance,
            context.status.salaries?.at(index)?.nomNaissance?.$status,
            `salaries.${index}.nomNaissance`,
          )
          addObligatoire(
            result,
            prop.prenom,
            context.status.salaries?.at(index)?.prenom?.$status,
            `salaries.${index}.prenom`,
          )
          addObligatoire(
            result,
            prop.dateNaissance,
            context.status.salaries?.at(index)?.dateNaissance?.$status,
            `salaries.${index}.dateNaissance`,
          )
          addObligatoire(result, prop.sexe, context.status.salaries?.at(index)?.sexe?.$status, `salaries.${index}.sexe`)
          addObligatoire(
            result,
            prop.nirTemporaire,
            context.status.salaries?.at(index)?.nirTemporaire?.$status,
            `salaries.${index}.nirTemporaire`,
          )
          addObligatoire(result, prop.nir, context.status.salaries?.at(index)?.nir?.$status, `salaries.${index}.nir`)
          addObligatoire(
            result,
            prop.adresse?.voie,
            context.status.salaries?.at(index)?.adresse?.voie?.$status,
            `salaries.${index}.adresse.voie`,
          )
          addObligatoire(
            result,
            prop.adresse?.codePostal,
            context.status.salaries?.at(index)?.adresse?.codePostal?.$status,
            `salaries.${index}.adresse.codePostal`,
          )
          addObligatoire(
            result,
            prop.adresse?.commune,
            context.status.salaries?.at(index)?.adresse?.commune?.$status,
            `salaries.${index}.adresse.commune`,
          )
          addObligatoire(
            result,
            prop.telephone,
            context.status.salaries?.at(index)?.telephone?.$status,
            `salaries.${index}.telephone`,
          )
          addObligatoire(
            result,
            prop.email,
            context.status.salaries?.at(index)?.email?.$status,
            `salaries.${index}.email`,
          )
          addObligatoire(
            result,
            prop.natureContrat,
            context.status.salaries?.at(index)?.natureContrat?.$status,
            `salaries.${index}.natureContrat`,
          )
          addObligatoire(
            result,
            prop.niveauFormation,
            context.status.salaries?.at(index)?.niveauFormation?.$status,
            `salaries.${index}.niveauFormation`,
          )
          addObligatoire(
            result,
            prop.numeroDossierCpf,
            context.status.salaries?.at(index)?.numeroDossierCpf?.$status,
            `salaries.${index}.numeroDossierCpf`,
          )
          addObligatoire(
            result,
            prop.beneficieReconnaissanceTravailleurHandicape,
            context.status.salaries?.at(index)?.beneficieReconnaissanceTravailleurHandicape?.$status,
            `salaries.${index}.beneficieReconnaissanceTravailleurHandicape`,
          )
          addObligatoire(result, prop.csp, context.status.salaries?.at(index)?.csp?.$status, `salaries.${index}.csp`)
          addObligatoire(
            result,
            prop.statut,
            context.status.salaries?.at(index)?.statut?.$status,
            `salaries.${index}.statut`,
          )
          addObligatoire(
            result,
            prop.pctTauxCharge,
            context.status.salaries?.at(index)?.pctTauxCharge?.$status,
            `salaries.${index}.pctTauxCharge`,
          )
          addObligatoire(
            result,
            prop.eligibilite,
            context.status.salaries?.at(index)?.eligibilite?.$status,
            `salaries.${index}.eligibilite`,
          )
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleNbModule: {
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}
        if (model.formation?.modules?.length == 0) {
          result[`formation.modules`] = 'Un module est obligatoire'
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleDureeFormationTotale: {
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}
        const dureeTotale = model.formation!.modules!.reduce((sum, mod) => sum + (mod.duree ?? 0), 0) ?? 0
        let dureeMax = 0
        if (model.sousDispositif === 'TuteurMAitreApprentissage') dureeMax = 40

        if (model.sousDispositif === 'Fse') dureeMax = 1637

        if (dureeMax !== 0 && dureeTotale > dureeMax) {
          for (const [index, _prop] of model.formation!.modules!.entries()) {
            result[`formation.modules.${index}.duree`] =
              `La somme des durées de la formation doit être inférieure ou égale à ${dureeMax} heures`
          }
        }

        let dureeMin = 0

        if (model.sousDispositif === 'Fse') dureeMin = 14

        if (dureeMin !== 0 && dureeTotale < dureeMin) {
          for (const [index, _prop] of model.formation!.modules!.entries()) {
            result[`formation.modules.${index}.duree`] =
              `La durée totale de la formation ne peut pas être inférieure à ${dureeMin} heures`
          }
        }

        return result
      },
      level: LevelRegleEnum.Metier,
    },
    controleObligatoireModules: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}

        addObligatoire(
          result,
          model.formation?.objectifFormation,
          context.status.formation?.objectifFormation?.$status,
          `formation.objectifFormation`,
        )

        if (isNullOrUndefined(model.formation) || isNullOrUndefined(model.formation?.modules)) return result

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (prop.typeModule === 'Externe' && isNullOrUndefined(prop.etablissement?.siret?.valeur)) {
            result[`formation.modules.${index}.etablissement`] = SAISIE_OBLIGATOIRE
          }

          addObligatoire(
            result,
            prop.modalitePedagogique,
            context.status.formation?.modules?.at(index)?.modalitePedagogique?.$status,
            `formation.modules.${index}.modalitePedagogique`,
          )
          addObligatoire(
            result,
            prop.dureeFoad,
            context.status.formation?.modules?.at(index)?.dureeFoad?.$status,
            `formation.modules.${index}.dureeFoad`,
          )
          addObligatoire(
            result,
            prop.duree,
            context.status.formation?.modules?.at(index)?.duree?.$status,
            `formation.modules.${index}.duree`,
          )
          addObligatoire(
            result,
            prop.dateDebut,
            context.status.formation?.modules?.at(index)?.dateDebut?.$status,
            `formation.modules.${index}.dateDebut`,
          )
          addObligatoire(
            result,
            prop.dateFin,
            context.status.formation?.modules?.at(index)?.dateFin?.$status,
            `formation.modules.${index}.dateFin`,
          )
          addObligatoire(
            result,
            prop.typeQualification,
            context.status.formation?.modules?.at(index)?.typeQualification?.$status,
            `formation.modules.${index}.typeQualification`,
          )
          addObligatoire(
            result,
            prop.typeReferentielFormation,
            context.status.formation?.modules?.at(index)?.typeReferentielFormation?.$status,
            `formation.modules.${index}.typeReferentielFormation`,
          )

          addObligatoire(
            result,
            prop.avecSubrogation,
            context.status.formation?.modules?.at(index)?.avecSubrogation?.$status,
            `formation.modules.${index}.avecSubrogation`,
          )
          addObligatoire(
            result,
            prop.niveauCertificationVisee,
            context.status.formation?.modules?.at(index)?.niveauCertificationVisee?.$status,
            `formation.modules.${index}.niveauCertificationVisee`,
          )
          addObligatoire(
            result,
            prop.objectifPrestataire,
            context.status.formation?.modules?.at(index)?.objectifPrestataire?.$status,
            `formation.modules.${index}.objectifPrestataire`,
          )
          addObligatoire(
            result,
            prop.typeFormation,
            context.status.formation?.modules?.at(index)?.typeFormation?.$status,
            `formation.modules.${index}.typeFormation`,
          )
          addObligatoire(
            result,
            prop.intitule,
            context.status.formation?.modules?.at(index)?.intitule?.$status,
            `formation.modules.${index}.intitule`,
          )
          addObligatoire(
            result,
            prop.rncp,
            context.status.formation?.modules?.at(index)?.rncp?.$status,
            `formation.modules.${index}.rncp`,
          )
          // PRT-585 manque régle sur formacode
          addObligatoire(
            result,
            prop.formacode,
            context.status.formation?.modules?.at(index)?.formacode?.$status,
            `formation.modules.${index}.formacode`,
          )
          // PRT-1038 manque règle sur repertoireSpecifique
          addObligatoire(
            result,
            prop.repertoireSpecifique,
            context.status.formation?.modules?.at(index)?.repertoireSpecifique?.$status,
            `formation.modules.${index}.repertoireSpecifique`,
          )

          if (context.status.formation?.modules?.at(index)?.lieuFormation?.$status === PropertyStatus.Required) {
            addObligatoire(
              result,
              prop.lieuFormation?.voie,
              context.status.formation?.modules?.at(index)?.lieuFormation?.voie?.$status,
              `formation.modules.${index}.lieuFormation.voie`,
            )
            addObligatoire(
              result,
              prop.lieuFormation?.codePostal,
              context.status.formation?.modules?.at(index)?.lieuFormation?.codePostal?.$status,
              `formation.modules.${index}.lieuFormation.codePostal`,
            )
            addObligatoire(
              result,
              prop.lieuFormation?.commune,
              context.status.formation?.modules?.at(index)?.lieuFormation?.commune?.$status,
              `formation.modules.${index}.lieuFormation.commune`,
            )
            addObligatoire(
              result,
              prop.lieuFormation?.complement,
              context.status.formation?.modules?.at(index)?.lieuFormation?.complement?.$status,
              `formation.modules.${index}.lieuFormation.complement`,
            )
          }

          for (const [indexFrais, propFrais] of model.formation!.modules!.at(index)!.fraisAnnexesSalarie!.entries()) {
            addObligatoire(
              result,
              propFrais.montant,
              context.status.formation?.modules?.at(index)?.fraisAnnexesSalarie?.at(indexFrais)?.montant?.$status,
              `formation.modules.${index}.fraisAnnexesSalarie.${indexFrais}.montant`,
            )
            addObligatoire(
              result,
              propFrais.quantite,
              context.status.formation?.modules?.at(index)?.fraisAnnexesSalarie?.at(indexFrais)?.quantite?.$status,
              `formation.modules.${index}.fraisAnnexesSalarie.${indexFrais}.quantite`,
            )
          }

          for (const [indexFrais, propFrais] of model.formation!.modules!.at(index)!.fraisPedagogiques!.entries()) {
            addObligatoire(
              result,
              propFrais.montant,
              context.status.formation?.modules?.at(index)?.fraisPedagogiques?.at(indexFrais)?.montant?.$status,
              `formation.modules.${index}.fraisPedagogiques.${indexFrais}.montant`,
            )
            addObligatoire(
              result,
              propFrais.quantite,
              context.status.formation?.modules?.at(index)?.fraisPedagogiques?.at(indexFrais)?.quantite?.$status,
              `formation.modules.${index}.fraisPedagogiques.${indexFrais}.quantite`,
            )
          }
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleEtablissementPrestataireActif: {
      validateur: (model, _gModel, _context) => {
        if (isNullOrUndefined(model.formation) || isNullOrUndefined(model.formation?.modules)) return

        const result: ModelValidateurResult<ActionFormationDTO> = {}
        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (
            prop.typeModule === 'Externe' &&
            isNotNullUndefinedOrEmptyString(prop.etablissement?.siret?.valeur) &&
            prop.etablissement.etatAdministratif === 'F'
          ) {
            result[`formation.modules.${index}.etablissement.siret.valeur`] = false
          }
        }

        return result
      },
      message: 'Vous ne pouvez pas sélectionner un établissement inactif, merci de sélectionner un autre établissement',
      level: LevelRegleEnum.Technique,
    },
    controleCoutMinimum: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}

        if (
          isNullOrUndefined(model.formation) ||
          isNullOrUndefined(model.formation?.modules) ||
          context.typeContext !== TypesContextesAutorisesEnum.Adherent ||
          (model.sousDispositif !== 'PDC' &&
            model.sousDispositif !== 'ActionStandard' &&
            model.sousDispositif !== 'AfFpe')
        )
          return result

        const coutPeda =
          model.formation?.modules?.reduce(
            (sum, module) =>
              sum + (module.fraisPedagogiques?.reduce((sum1, frais) => sum1 + (frais.montant ?? 0), 0) ?? 0),
            0,
          ) ?? 0

        const coutFrais =
          model.formation?.modules?.reduce(
            (sum, module) =>
              sum + (module.fraisAnnexesSalarie?.reduce((sum1, frais) => sum1 + (frais.montant ?? 0), 0) ?? 0),
            0,
          ) ?? 0

        const rem = model.salaries?.reduce((sum, sal) => sum + (sal.tauxHoraireBrut ?? 0), 0) ?? 0

        if (coutPeda + coutFrais + rem === 0) {
          for (const [index, _prop] of model.formation!.modules!.entries()) {
            result[`formation.modules.${index}.fraisPedagogiques`] = false
            result[`formation.modules.${index}.fraisAnnexesSalarie`] = false
          }
        }

        return result
      },
      message:
        'Le montant total des coûts (Coût pédagogiques, coût de formation, Autres frais, frais annexes et rémunération) ne doit pas être égal à 0.',
      level: LevelRegleEnum.Transmettre,
    },
    controleFichier: {
      validateur: (model, _globalModel, context) => {
        if (isAdherentOuMandatGestion(context.typeContext, false) !== true) return

        for (const doc of context.naturesFichiers.obligatoires) {
          if (model.fichiers?.some((x) => x.nature === doc) !== true) return { fichiers: false }
        }
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    /* // Régle en doublon
    controleFinancementObligatoire: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ActionFormationDTO> = {}

        if (context.status.financement?.$status === PropertyStatus.Required && isNullOrUndefined(model.financement))
          result['financement'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    */
  },
} as const
